import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { TrendingUp, Mail, ArrowLeft, Loader2, RefreshCw, Check, Shield, FileText } from 'lucide-react';
import { toast } from 'sonner';
import API from '../utils/api';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      // Check if email needs verification
      if (err.message === 'email_not_verified') {
        setShowVerification(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (showVerification) {
    return <VerificationPage email={email} onBack={() => setShowVerification(false)} />;
  }

  return (
    <div className="min-h-screen bg-background grid-bg flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TrendingUp className="h-8 w-8 text-primary" />
            <span className="text-2xl font-heading font-bold">TradeJourn<span className="text-primary">.ai</span></span>
          </div>
          <CardTitle className="text-xl">{t('login')}</CardTitle>
          <CardDescription>{t('tradingJournal')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={loginWithGoogle}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('continueWithGoogle')}
          </Button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">{t('or')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('login')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            {t('dontHaveAccount')}{' '}
            <Link to="/register" className="text-primary hover:underline">
              {t('register')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showGoogleTermsModal, setShowGoogleTermsModal] = useState(false);
  const { loginWithGoogle } = useAuth();
  const { t, language } = useLanguage();

  const handleGoogleRegister = () => {
    // Show terms modal before Google login
    setShowGoogleTermsModal(true);
  };

  const handleGoogleTermsAccepted = () => {
    setShowGoogleTermsModal(false);
    loginWithGoogle();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!acceptedTerms) {
      setError(language === 'de' 
        ? 'Bitte akzeptiere die AGB und Datenschutzerklärung.' 
        : 'Please accept the terms and privacy policy.');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name, language, accepted_terms: true }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registrierung fehlgeschlagen');
      }
      
      // Registration successful - show verification screen
      setShowVerification(true);
      toast.success('Verifizierungscode wurde gesendet!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (showVerification) {
    return <VerificationPage email={email} onBack={() => setShowVerification(false)} />;
  }

  return (
    <div className="min-h-screen bg-background grid-bg flex items-center justify-center p-4">
      {/* Google Terms Modal */}
      <Dialog open={showGoogleTermsModal} onOpenChange={setShowGoogleTermsModal}>
        <DialogContent className="glass max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-center gap-2 mb-2">
              <TrendingUp className="h-6 w-6 text-primary" />
              <span className="text-lg font-heading font-bold">TradeJourn<span className="text-primary">.ai</span></span>
            </div>
            <DialogTitle className="text-center">
              {language === 'de' ? 'Nutzungsbedingungen' : 'Terms of Service'}
            </DialogTitle>
            <DialogDescription className="text-center">
              {language === 'de' 
                ? 'Bitte bestätige, dass du unsere Nutzungsbedingungen gelesen hast und ihnen zustimmst.'
                : 'Please confirm that you have read and agree to our terms of service.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/50">
              <FileText className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">{language === 'de' ? 'Allgemeine Geschäftsbedingungen' : 'Terms and Conditions'}</p>
                <Link to="/agb" target="_blank" className="text-xs text-primary hover:underline">
                  {language === 'de' ? 'AGB lesen →' : 'Read Terms →'}
                </Link>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/50">
              <Shield className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">{language === 'de' ? 'Datenschutzerklärung' : 'Privacy Policy'}</p>
                <Link to="/datenschutz" target="_blank" className="text-xs text-primary hover:underline">
                  {language === 'de' ? 'Datenschutz lesen →' : 'Read Privacy Policy →'}
                </Link>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowGoogleTermsModal(false)} 
              className="flex-1"
            >
              {language === 'de' ? 'Abbrechen' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleGoogleTermsAccepted} 
              className="flex-1"
            >
              <Check className="h-4 w-4 mr-2" />
              {language === 'de' ? 'Akzeptieren & Fortfahren' : 'Accept & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TrendingUp className="h-8 w-8 text-primary" />
            <span className="text-2xl font-heading font-bold">TradeJourn<span className="text-primary">.ai</span></span>
          </div>
          <CardTitle className="text-xl">{t('register')}</CardTitle>
          <CardDescription>{t('tradingJournal')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleRegister}
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('continueWithGoogle')}
          </Button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">{t('or')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="register-password-input"
              />
            </div>
            
            {/* Terms and Privacy Checkbox */}
            <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/30 border border-zinc-700/50">
              <Checkbox 
                id="terms" 
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked)}
                className="mt-0.5"
                data-testid="register-terms-checkbox"
              />
              <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                {language === 'de' ? (
                  <>
                    Ich habe die{' '}
                    <Link to="/agb" target="_blank" className="text-primary hover:underline">AGB</Link>
                    {' '}und{' '}
                    <Link to="/datenschutz" target="_blank" className="text-primary hover:underline">Datenschutzerklärung</Link>
                    {' '}gelesen und stimme diesen zu.
                  </>
                ) : (
                  <>
                    I have read and agree to the{' '}
                    <Link to="/agb" target="_blank" className="text-primary hover:underline">Terms</Link>
                    {' '}and{' '}
                    <Link to="/datenschutz" target="_blank" className="text-primary hover:underline">Privacy Policy</Link>.
                  </>
                )}
              </label>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || !acceptedTerms} 
              data-testid="register-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('register')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            {t('alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline">
              {t('login')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Verification Page Component
const VerificationPage = ({ email, onBack }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace - go to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Bitte gib den vollständigen 6-stelligen Code ein');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, code: fullCode }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verifizierung fehlgeschlagen');
      }
      
      // Verification successful - set user and redirect
      setUser(data);
      toast.success('E-Mail erfolgreich verifiziert!');
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
      // Clear code on error
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    
    try {
      const response = await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Fehler beim erneuten Senden');
      }
      
      toast.success('Neuer Code wurde gesendet!');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid-bg flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-xl">E-Mail verifizieren</CardTitle>
          <CardDescription>
            Wir haben einen 6-stelligen Code an<br />
            <span className="font-medium text-foreground">{email}</span><br />
            gesendet
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md text-center">
              {error}
            </div>
          )}
          
          {/* 6-digit code input */}
          <div className="flex justify-center gap-2" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-mono font-bold"
                data-testid={`verification-code-${index}`}
              />
            ))}
          </div>
          
          <Button 
            onClick={handleVerify} 
            className="w-full" 
            disabled={loading || code.join('').length !== 6}
            data-testid="verify-submit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {loading ? 'Verifiziere...' : 'Verifizieren'}
          </Button>
          
          <div className="text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              Keinen Code erhalten?
            </p>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleResend}
              disabled={resending}
              data-testid="resend-code-btn"
            >
              {resending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Code erneut senden
            </Button>
          </div>
          
          <Button 
            variant="outline" 
            className="w-full"
            onClick={onBack}
            data-testid="back-to-login-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
